import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

/**
 * Animates a number toward `target` whenever it changes.
 *
 * Tweens from the *previous* target rather than from zero, so a slider drag
 * reads as the figure climbing rather than restarting each time. Under
 * reduced motion the target is adopted immediately.
 */
export function useCountUp(target: number, duration = 700): number {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState(target);
  const previous = useRef(target);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      previous.current = target;
      return;
    }

    const start = previous.current;
    const diff = target - start;
    const startedAt = performance.now();
    let raf = 0;

    const frame = (now: number): void => {
      const progress = Math.min((now - startedAt) / duration, 1);
      // Ease-out cubic.
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(start + diff * eased));

      if (progress < 1) raf = requestAnimationFrame(frame);
      else previous.current = target;
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, reduced]);

  return value;
}
